import { Mail } from "lucide-react";
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "../../contexts/LanguageContext";
import {
  AuthAlert,
  AuthButton,
  AuthField,
  AuthHeader,
  AuthInput,
  AuthLink,
  PasswordInput,
} from "./AuthPrimitives";

// Validação de email simples
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

interface LoginFormProps {
  onSwitchToRegister: () => void;
  onForgotPassword?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToRegister,
  onForgotPassword,
}) => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  /** true quando a falha é rede/API inacessível (não senha incorreta) */
  const [isConnectionError, setIsConnectionError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: boolean;
    password?: boolean;
  }>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFieldErrors((prev) => ({ ...prev, [name]: false }));
    // Limpar erro quando usuário começar a digitar
    if (error) {
      setError("");
      setIsConnectionError(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.email.trim()) {
      setIsConnectionError(false);
      setFieldErrors({ email: true });
      setError(t("E-mail é obrigatório"));
      return false;
    }

    if (!validateEmail(formData.email)) {
      setIsConnectionError(false);
      setFieldErrors({ email: true });
      setError(t("E-mail inválido"));
      return false;
    }

    if (!formData.password.trim()) {
      setIsConnectionError(false);
      setFieldErrors({ password: true });
      setError(t("Senha é obrigatória"));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setError("");
    setIsConnectionError(false);
    setIsLoading(true);

    try {
      // Normaliza a caixa do e-mail (defesa em profundidade - o backend já compara
      // case-insensitive, mas teclados mobile costumam autocapitalizar o e-mail).
      await login(
        formData.email.trim().toLowerCase(),
        formData.password,
        rememberMe,
      );
      // Login bem-sucedido
      // Manter loading por mais alguns segundos para mostrar "Autenticando..."
      setTimeout(() => {
        setIsLoading(false);
      }, 2000); // 2 segundos para mostrar "Autenticando..."
    } catch (error: any) {
      console.error("Erro no login:", error);

      const rawMessage = String(error?.message || "");
      const message = rawMessage.toLowerCase();

      // Falha de rede / API inacessível (não é senha errada)
      const connectionFailed =
        error?.name === "TypeError" ||
        error?.name === "AbortError" ||
        message.includes("failed to fetch") ||
        message.includes("network") ||
        message.includes("load failed") ||
        message.includes("servidor não respondeu") ||
        message.includes("comunicação") ||
        message.includes("conexão interrompida") ||
        message.includes("interrupção na conexão");

      // Traduzir mensagens de erro técnicas para português
      let errorMessage = t("Erro ao fazer login");

      if (rawMessage) {
        if (connectionFailed) {
          errorMessage = import.meta.env.DEV
            ? t(
                "Não foi possível conectar ao servidor da API. Confirme se o backend está rodando e se a URL em VITE_APP_API_URL aponta para o host e porta corretos.",
              )
            : t(
                "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.",
              );
        } else if (message.includes("invalid email or password")) {
          errorMessage = t("E-mail ou senha incorretos");
        } else if (message.includes("user account is not active")) {
          errorMessage = t("Conta desativada. Entre em contato com o suporte");
        } else if (message.includes("timeout")) {
          errorMessage = t("Tempo limite excedido. Tente novamente");
        } else if (message.includes("500")) {
          errorMessage = t(
            "Erro interno do servidor. Tente novamente mais tarde",
          );
        } else {
          errorMessage = rawMessage;
        }
      }

      setError(errorMessage);
      // Falha de rede não é culpa dos campos; credenciais incorretas marcam os dois.
      setFieldErrors(connectionFailed ? {} : { email: true, password: true });
      setIsConnectionError(connectionFailed);
      setIsLoading(false); // Parar loading imediatamente em caso de erro
    }
  };

  const hint = isConnectionError
    ? import.meta.env.DEV
      ? t(
          "Dica: no navegador, teste http://127.0.0.1:8000/api/health (ajuste host/porta se o seu backend for outro).",
        )
      : undefined
    : t("Verifique suas credenciais e tente novamente.");

  return (
    <div className="space-y-5 [@media(max-height:900px)]:space-y-3">
      <AuthHeader
        title={t("Bem-vindo de volta")}
        subtitle={t(
          "Acesse sua conta corporativa para gerenciar suas redes e campanhas.",
        )}
      />

      {error && (
        <AuthAlert variant="error" title={t("Erro ao entrar")} message={error}>
          {hint && <p className="text-xs opacity-80">{hint}</p>}
        </AuthAlert>
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-4 [@media(max-height:900px)]:space-y-3"
        noValidate
      >
        <AuthField label={t("Endereço de e-mail")} htmlFor="email" required>
          <AuthInput
            type="email"
            id="email"
            name="email"
            icon={Mail}
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
            placeholder={t("joao.silva@empresa.com")}
            error={fieldErrors.email}
            required
          />
        </AuthField>

        <AuthField
          label={t("Senha de acesso")}
          htmlFor="password"
          required
          aside={
            onForgotPassword && (
              <AuthLink onClick={onForgotPassword} className="text-xs">
                {t("Esqueceu a senha?")}
              </AuthLink>
            )
          }
        >
          <PasswordInput
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            autoComplete="current-password"
            placeholder={t("Sua senha corporativa")}
            error={fieldErrors.password}
            required
          />
        </AuthField>

        <label className="group flex cursor-pointer select-none items-center gap-2.5 text-sm text-slate-600">
          <span className="relative flex items-center">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="peer sr-only"
            />
            <span className="h-4 w-7 rounded-full bg-slate-200 transition-colors peer-checked:bg-violet-600 peer-focus-visible:ring-2 peer-focus-visible:ring-violet-500/30" />
            <span className="absolute left-[2px] top-[2px] h-3 w-3 rounded-full bg-white transition-transform peer-checked:translate-x-3" />
          </span>
          <span className="transition-colors group-hover:text-slate-800">
            {t("Manter-me conectado neste dispositivo")}
          </span>
        </label>

        <AuthButton
          type="submit"
          loading={isLoading}
          loadingText={t("Autenticando...")}
          arrow
          data-testid="login-button"
        >
          {t("Entrar na plataforma")}
        </AuthButton>
      </form>

      <p className="text-center text-sm text-slate-600">
        {t("Ainda não possui uma conta?")}{" "}
        <AuthLink onClick={onSwitchToRegister}>
          {t("Criar conta corporativa")}
        </AuthLink>
      </p>
    </div>
  );
};
